import { useWebBaseURL } from './useWebBaseURL';
import { isNil, isWeb } from '../utils';
import {
  EMBEDDED_WEB_QUERY_KEY,
  GLOBAL_LAYOUT_APP,
  GLOBAL_LAYOUT_QUERY_KEY,
} from '../constants/webviewQuery';

/**
 * 앱 안에서 열 웹뷰 URL 을 만든다.
 *
 * 웹뷰 화면들이 각자 `useWebBaseURL()` + `new URL()` + 쿼리 조립 + `global_layout=app` 을
 * 반복하고 있어 한 곳으로 모았다.
 *
 * @param pathOrUrl 목적지. path(`/orders/123`) 또는 절대 URL 둘 다 받는다.
 * @param query 웹뷰 URL 에 얹을 쿼리. `undefined` 값은 건너뛴다.
 *   `global_layout` 은 마지막에 강제로 세팅되므로 이 값으로는 덮을 수 없다.
 *
 * origin 은 `useWebBaseURL()` 이 접속 환경으로 판별한다 — prod / 스테이징 / 로컬(:3000).
 * 로컬은 `getWebBaseURLSync` 가 접속 hostname 에 :3000 을 붙이므로, IP 로 접속한 경우
 * (휴대폰에서 LAN 접속)에도 개발 서버에 닿는다. 화면마다 `__DEV__` 로 host 를 갈아끼우던
 * 코드는 그래서 필요 없다.
 *
 * ⚠️ useMemo 를 쓰지 않는다. query 가 인라인 객체로 넘어와 매 렌더 identity 가 바뀌므로
 * 메모가 무효하고, 문자열 조립 비용은 무시할 수준이다. 반환값이 같은 문자열이면 iframe
 * `src` 도 그대로라 DOM 변화가 없다.
 */
export const useWebviewUri = (
  pathOrUrl: string | undefined,
  query?: Record<string, string | number | undefined>,
): string | undefined => {
  const webBaseUrl = useWebBaseURL();

  if (isNil(pathOrUrl) || isNil(webBaseUrl)) return undefined;

  const url = new URL(pathOrUrl, webBaseUrl);

  Object.entries(query ?? {}).forEach(([key, value]) => {
    if (isNil(value)) return;
    url.searchParams.set(key, String(value));
  });

  // 콘텐츠가 앱 레이아웃(웹 header/footer 제거)으로 렌더하도록 알린다.
  url.searchParams.set(GLOBAL_LAYOUT_QUERY_KEY, GLOBAL_LAYOUT_APP);

  // 웹에서는 콘텐츠가 iframe 안에 들어간다. 콘텐츠가 프레임 구조를
  // (`window.self !== window.top`) 추측하지 않도록 호스트가 명시적으로 알린다.
  // 네이티브는 앱 웹뷰라 콘텐츠가 UA 로 이미 앱을 판별하므로 붙이지 않는다.
  if (isWeb) {
    url.searchParams.set(EMBEDDED_WEB_QUERY_KEY, 'true');
  }

  return url.toString();
};
